import React, { useState } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "../context/ToastContext";

const ChangePassword = () => {
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        currentPassword: "",
        password: "",
        confirmPassword: "",
    });

    const [errors, setErrors] = useState({});

    const { success, error } = useToast();

    const handleChange = (e) => {
        const { name, value } = e.target;

        const updatedData = { ...formData, [name]: value };
        setFormData(updatedData);

        setErrors((prev) => ({
            ...prev,
            [name]: validateField(name, value, updatedData),
        }));

        // Re-validate confirm password if password changes
        if (name === "password" && formData.confirmPassword) {
            setErrors((prev) => ({
                ...prev,
                confirmPassword: validateField(
                    "confirmPassword",
                    formData.confirmPassword,
                    updatedData
                ),
            }));
        }
    };

    const validateField = (name, value, data) => {
        switch (name) {
            case "currentPassword":
                if (!value) return "Current password is required";
                break;

            case "password":
                if (!value) return "Password is required";
                if (value.length < 6)
                    return "Password must be at least 6 characters";
                break;

            case "confirmPassword":
                if (!value) return "Confirm password is required";
                if (value !== data.password)
                    return "Passwords do not match";
                break;

            default:
                return "";
        }

        return "";
    };


    const validate = () => {
        const newErrors = {};

        Object.keys(formData).forEach((key) => {
            const error = validateField(key, formData[key], formData);
            if (error) newErrors[key] = error;
        });

        return newErrors;
    };



    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }


        setLoading(true);

        // Simulate API call
        setTimeout(() => {
            setLoading(false);

            success("Password updated successfully!");

            console.log("formData", formData)

            setFormData({
                currentPassword: "",
                password: "",
                confirmPassword: "",
            });

            setErrors({});
        }, 1500);
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-semibold text-gray-800">
                    Change Password
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                    Update your password to keep your account secure.
                </p>
            </div>

            <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-6">
                <div className="space-y-6">

                    {/* Current Password */}
                    <div className="relative">
                        <label className="text-sm text-gray-600">
                            Current Password
                        </label>
                        <input
                            type={showCurrent ? "text" : "password"}
                            name="currentPassword"
                            value={formData.currentPassword}
                            onChange={handleChange}
                            placeholder="Enter current password"
                            className={`w-full mt-1 px-4 py-3 rounded-xl border pr-12
                                ${errors.currentPassword ? "border-red-500" : "border-gray-200"}
                                focus:ring-1 focus:ring-black focus:border-black outline-none`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowCurrent(!showCurrent)}
                            className="absolute right-4 top-[42px] text-gray-500"
                        >
                            {showCurrent ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        {errors.currentPassword && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.currentPassword}
                            </p>
                        )}
                    </div>

                    {/* New Password */}
                    <div className="relative">
                        <label className="text-sm text-gray-600">
                            New Password
                        </label>
                        <input
                            type={showNew ? "text" : "password"}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter new password"
                            className={`w-full mt-1 px-4 py-3 rounded-xl border pr-12
                                ${errors.password ? "border-red-500" : "border-gray-200"}
                                focus:ring-1 focus:ring-black focus:border-black outline-none`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowNew(!showNew)}
                            className="absolute right-4 top-[42px] text-gray-500"
                        >
                            {showNew ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        {errors.password && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.password}
                            </p>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div className="relative">
                        <label className="text-sm text-gray-600">
                            Confirm Password
                        </label>
                        <input
                            type={showConfirm ? "text" : "password"}
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            placeholder="Confirm new password"
                            className={`w-full mt-1 px-4 py-3 rounded-xl border pr-12
                                ${errors.confirmPassword ? "border-red-500" : "border-gray-200"}
                                focus:ring-1 focus:ring-black focus:border-black outline-none`}
                        />
                        <button
                            type="button"
                            onClick={() => setShowConfirm(!showConfirm)}
                            className="absolute right-4 top-[42px] text-gray-500"
                        >
                            {showConfirm ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        {errors.confirmPassword && (
                            <p className="text-red-500 text-xs mt-1">
                                {errors.confirmPassword}
                            </p>
                        )}
                    </div>

                </div>

                <div className="mt-8 flex justify-end">
                    <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="bg-black hover:bg-gray-900 disabled:opacity-70 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl transition flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin" size={18} />
                                Updating...
                            </>
                        ) : (
                            "Update Password"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangePassword;
